import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoMdArchive } from "react-icons/io";
import CustomerService from "../../../../services/CustomerService";
import { toast } from "react-toastify";
import { colors } from "../../../config/colors";

const ViewCustomer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        const response = await CustomerService.getCustomerById(id);
        if (response.data.success) {
          setCustomer(response.data.data);
        } else {
          throw new Error("Failed to fetch customer details");
        }
      } catch (error) {
        console.error("Error fetching customer details:", error);
        toast.error("Failed to fetch customer details");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerDetails();
  }, [id]);

  const handleClose = () => {
    navigate("/customer-list");
  };

  const handleEdit = () => {
    navigate(`/edit-customer/${id}`);
  };

  const handleArchiveCustomer = async () => {
    try {
      console.log(customer, "customer");
      setArchiving(true);
      const response = await CustomerService.archiveCustomer(customer._id, true);
      if (response.data.success) {
        toast.success("Customer archived successfully");
        navigate("/customer-list");
      } else {
        throw new Error(response.data.error || "Failed to archive customer");
      }
    } catch (error) {
      console.error("Archive error response data:", error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to archive customer";
      // toast.error(errorMessage);
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchiveCustomer = async () => {
    try {
      setArchiving(true);
      const response = await CustomerService.archiveCustomer(customer._id, false);
      if (response.data.success) {
        toast.success("Customer unarchived successfully");
        navigate("/customer-list");
      } else {
        throw new Error("Failed to unarchive customer");
      }
    } catch (error) {
      console.error("Error unarchiving customer:", error);
      toast.error("Failed to unarchive customer");
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-4 mt-5 pt-5">
        <div className="w-8 h-8 border-4 border-gray-300 rounded-full animate-spin" style={{ borderTopColor: colors.primary.headingsBody }}></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex flex-col items-center mt-5 pt-5">
        <h2 className="text-xl font-semibold text-gray-800">Customer not found</h2>
        <button
          onClick={handleClose}
          className="mt-3 inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
          style={{ backgroundColor: colors.highlights.callToAction }}
        >
          Back to List
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
        <h4 className="text-xl font-semibold" style={{ color: colors.primary.headingsBody }}>Customer Details</h4>
        <div className="flex gap-3">
          {!customer?.isArchived && (
            <button
              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
              style={{ backgroundColor: colors?.warning?.dark || '#f59e0b' }}
              onClick={handleArchiveCustomer}
              disabled={archiving}
            >
              {archiving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : (
                <IoMdArchive className="h-4 w-4 mr-2" />
              )}
              <span className="text-sm">{archiving ? 'Archiving...' : 'Archive Customer'}</span>
            </button>
          )}
          {customer?.isArchived && (
            <button
              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
              style={{ backgroundColor: colors.success.dark }}
              onClick={handleUnarchiveCustomer}
              disabled={archiving}
            >
              {archiving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : (
                <IoMdArchive className="h-4 w-4 mr-2" />
              )}
              <span className="text-sm">{archiving ? 'Unarchiving...' : 'Unarchive Customer'}</span>
            </button>
          )}
          {!customer?.isArchived && (
            <button
              onClick={handleEdit}
              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
              style={{ backgroundColor: colors.highlights.callToAction }}
            >
              Edit Customer
            </button>
          )}
          <button
            onClick={handleClose}
            className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
            style={{ backgroundColor: colors.darkAccents }}
          >
            Back to List
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-gray-300 rounded-full animate-spin" style={{ borderTopColor: colors.primary.headingsBody }}></div>
        </div>
      ) : customer ? (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Name</label>
                <div className="mt-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-900">
                  {customer.name}
                </div>
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Email</label>
                <div className="mt-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-900">
                  {customer.email}
                </div>
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Phone</label>
                <div className="mt-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-900">
                  {customer.phoneNumber}
                </div>
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Status</label>
                <div className="mt-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-900">
                  {customer.isArchived ? "Archived" : (customer.isActive ? "Active" : "Inactive")}
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold mt-8 mb-4" style={{ color: colors.primary.headingsBody }}>Address</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Street</label>
                <div className="mt-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-900">
                  {customer.street}
                </div>
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>City</label>
                <div className="mt-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-900">
                  {customer.city}
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold mt-8 mb-4" style={{ color: colors.primary.headingsBody }}>Additional Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Created At</label>
                <div className="mt-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-900">
                  {new Date(customer.createdAt).toLocaleString()}
                </div>
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium" style={{ color: colors.primary.headingsBody }}>Updated At</label>
                <div className="mt-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-900">
                  {new Date(customer.updatedAt).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          Customer not found
        </div>
      )}
    </div>
  );
};

export default ViewCustomer;
